import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import type { RestaurantPartner } from '@shared/types/restaurant';
import { getCurrentDomainConfig } from '@core/tenant/getSubdomainPrefix';

// ---------------------------------------------------------------------------
// URL builder
// ---------------------------------------------------------------------------

function buildPartnerUrl(subdomainPrefix: string): string {
  if (import.meta.env.DEV) {
    return `http://localhost:8080?r=${subdomainPrefix}`;
  }

  const config = getCurrentDomainConfig();
  const baseDomain = config?.baseDomain ?? 'meitre.com';

  return `https://${subdomainPrefix}.${baseDomain}`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PartnerSuggestion {
  partner: RestaurantPartner;
  hasAvailability: boolean;
}

interface PartnerRestaurantSuggestionsProps {
  suggestions: PartnerSuggestion[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PartnerRestaurantSuggestions = ({
  suggestions,
}: PartnerRestaurantSuggestionsProps) => {
  const { t } = useTranslation('booking');

  if (suggestions.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="border-t border-border mb-6" />
      <p className="text-foreground font-medium text-center mb-4">
        {t('OTHER_RESTAURANTS')}
      </p>
      <div className="flex flex-col gap-3">
        {suggestions.map((s) => {
          const subdomain = s.partner.subdomainPrefix;
          if (!subdomain) return null;

          const url = buildPartnerUrl(subdomain);

          return (
            <a
              key={s.partner.id}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-xl border border-border p-4 transition-colors hover:bg-foreground/5 hover:border-brand/50"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-sm truncate">
                  {s.partner.name}
                </p>
                {s.partner.address && (
                  <p className="text-xs text-foreground/50 truncate">
                    {s.partner.address}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <ExternalLink size={16} className="text-foreground/40" />
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(PartnerRestaurantSuggestions);
